import { useEffect, useMemo, useState } from 'react';
import { Col, Container, Modal, Row, Spinner, Stack } from 'react-bootstrap';
import { BsClock } from 'react-icons/bs';
import { FaMapMarkerAlt, FaTag } from 'react-icons/fa';
import { FaBolt } from 'react-icons/fa6';
import { LuChefHat } from 'react-icons/lu';
import { useNavigate, useParams } from 'react-router-dom';
import Slider from 'react-slick';
import { PageMeta } from '../../../../components/meta/PageMeta';
import SmilingKidsDefaultHero from '../../../../assets/images/smilingKidsDefaultHero.png';
import SspSmilingDefaultHero from '../../../../assets/images/sspSmilingDefaultHero.png';
import { BackButton } from '../../../../components/buttons/BackButton';
import { useGetUserProfileQuery } from '../../../../services/endpoints/people/user';
import { useGetCoursePublicQuery } from '../../../../services/endpoints/schedule/course';
import { isAuthenticated } from '../../../../services/helper';
import { Course } from '../../../../types/course.type';
import { Lesson } from '../../../../types/lesson.type';
import { getCourseSessionTimeDisplay } from '../../../../utils/dateUtils';
import { loginPathWithReturnTo } from '../../../../utils/returnTo';
import {
  formatAddress,
  formatToCurrency,
  formatTwoDates,
  generateFileUrlSegment,
  getClassesRemaining
} from '../../../../utils/utils';
import AddChild from '../../../user/AddChild';
import AddFavorite from './AddFavorite';
import CourseChildConfirmation from './CourseChildConfirmation';
import './CourseDetails.css';
import CourseDetailsAccordion from './CourseDetailsAccordion';
import CourseRegister from './CourseRegister';
import CourseRegisterButton from './CourseRegisterButton';

const DEFAULT_SESSION_HERO = SmilingKidsDefaultHero;

const THEME_HERO_BY_ID: Record<number, string> = {
  108: SspSmilingDefaultHero
};

const getSessionHeroFallback = (themeId?: number) =>
  (themeId != null && THEME_HERO_BY_ID[themeId]) || DEFAULT_SESSION_HERO;

interface CourseDetailsProps {}

const CourseDetails: React.FC<CourseDetailsProps> = ({}) => {
  const params = useParams();
  const [course, setCourse] = useState<Course>();
  const [registerSelectedChildIds, setRegisterSelectedChildIds] = useState<number[]>([]);
  const [showAddChild, setShowAddChild] = useState(false);
  const [showChildConfirmation, setShowChildConfirmation] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const { data, isLoading } = useGetCoursePublicQuery(params.id!);
    const navigate = useNavigate();

    // Arrived via a retired slug: swap the URL for the current one so shared
    // marketing links keep working and the address bar reflects the live title.
    useEffect(() => {
        const canonical = data?.course?.slug;
        if (canonical && params.id && params.id !== canonical && !/^\d+$/.test(params.id)) {
            navigate(`/sessions/${canonical}`, { replace: true });
        }
    }, [data?.course?.slug, params.id, navigate]);

  const { data: userProfile, refetch } = useGetUserProfileQuery();

  const [courseTimeDisplay, setCourseTimeDisplay] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!isLoading && data) {
      setCourse(data.course);
      const actionQS = new URLSearchParams(window.location.search).get('action');
      const wantsRegister = actionQS === 'edit' || actionQS === 'register';

      // Invite/edit deep links require auth — send logged-out users to login, then back here.
      if (wantsRegister && !isAuthenticated()) {
        const returnTo = `${window.location.pathname}${window.location.search}`;
        navigate(loginPathWithReturnTo(returnTo), { replace: true });
        return;
      }

      setShowRegister(wantsRegister);

      setCourseTimeDisplay(getCourseSessionTimeDisplay(data.course) || undefined);
    }
  }, [data, isLoading, navigate]);

  const hideOverlay = (showConfirmation: boolean) => {
    setShowAddChild(false);
    if (showConfirmation) {
      setShowChildConfirmation(true);
    }
  };

  const hideChildConfirmation = () => {
    setShowChildConfirmation(false);
  };

  const hideRegister = () => {
    setShowRegister(false);
    setRegisterSelectedChildIds([]);  // clear on actual close
  };

  const addChild = ({
                      closeConfirmation,
                      closeRegister,
                      getProfile
                    }: {
    closeConfirmation?: boolean;
    closeRegister?: boolean;
    getProfile?: boolean;
  }) => {
    if (closeConfirmation) {
      setShowChildConfirmation(false);
    }
    if (closeRegister) {
      setShowRegister(false);
    }
    if (getProfile) {
      refetch();
    }
    setShowAddChild(true);
  };

  const classesRemaining: Lesson[] = course ? getClassesRemaining(course) : [];

  const imgSlides = useMemo(() => {
    if (!course) return [];
    const themeImages = course.theme?.theme_images;
    // Wait for the theme payload (or a confirmed missing theme) so defaults don't flash first.
    const themePayloadReady = course.theme == null || Array.isArray(themeImages);
    if (!themePayloadReady) return [];

    const slides = (themeImages ?? [])
      .filter((img) => img.image_file_name && img.image_file_name !== 'empty.png')
      .map(
        (img) =>
          `${generateFileUrlSegment(img.id, img.s3_base_url, 'original')}/${img.image_file_name}`
      );
    if (slides.length) return slides;
    return [getSessionHeroFallback(course.theme_id ?? course.theme?.id)];
  }, [course]);
  const hasMultipleSlides = imgSlides.length > 1;
  const carouselSettings = {
    dots: hasMultipleSlides,
    infinite: hasMultipleSlides,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: hasMultipleSlides,
    autoplaySpeed: 4000
  };

  return (
      <>
        {course && (
            <PageMeta
                title={course.title}
                description={`Limited space available! Enroll your child in ${course.title} today.`}
            />
        )}
        {course?.venue?.location?.title && (
            <Container
                fluid
                className="text-blue bg-header d-flex gradient-background"
            >
              <div className="fruit-background left"></div>
              <div className="d-flex justify-content-center align-items-center fanchise-title-container">
                <h1 className="align-items-center text-center">{course?.venue?.location?.title}</h1>
              </div>
              <div className="fruit-background right"></div>
            </Container>
        )}
        {/* <div style={{ position: 'relative', width: '100%', height: '120px', overflow: 'hidden' }}>
        <img
          src={Banner}
          alt="course-image"
          className="img-fluid w-100"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#2B4E64',
            fontSize: '40px',
            fontWeight: 'bold',
            fontFamily: 'Nunito Sans'
          }}
        >
          {course?.venue?.title ?? ''}
        </div>
      </div> */}

        <div className="course-details-content">
          <BackButton
              text="Back"
              color="#2B4E64"
              size=".95rem"
          />
          {course && (
              <>
                <Row className="mt-2">
                  <Col
                      xs={12}
                      lg={4}
                  >
                    <div className="course-img-frame">
                      {imgSlides.length > 0 ? (
                        <Slider {...carouselSettings}>
                          {imgSlides.map((src, index) => (
                            <div
                              key={`${src}-${index}`}
                              className="course-img-slide"
                            >
                              <img
                                src={src}
                                alt={course.title}
                                className="course-img"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  if (target.dataset.fallbackApplied) return;
                                  target.dataset.fallbackApplied = 'true';
                                  target.src = getSessionHeroFallback(
                                    course.theme_id ?? course.theme?.id
                                  );
                                }}
                              />
                            </div>
                          ))}
                        </Slider>
                      ) : (
                        <div
                          className="d-flex justify-content-center align-items-center h-100"
                          role="status"
                        >
                          <Spinner animation="border" variant="primary" />
                          <span className="visually-hidden">Loading carousel images</span>
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col
                      xs={12}
                      lg={8}
                  >
                    <Stack
                        direction="vertical"
                        gap={2}
                    >
                  <span className={'h2 text-capitalize text-info fw-bold mb-0'}>
                    {course?.title}
                  </span>

                      {course?.payment_type === 'onsite' && (
                          <span className="d-flex w-100">
                      <FaBolt
                          size={24}
                          className="me-1"
                      />
                      <h5>
                        Hey Parents! Registration and payment for this session must be completed
                        directly with your school or organization.{' '}
                        <a href="/contact">Please contact us if you have any questions.</a>
                      </h5>
                    </span>
                      )}
                      <span className={'h4 text-capitalize text-secondary fw-bold mb-0'}>
                    {formatTwoDates(course?.starts_on, course?.ends_on)}
                  </span>
                      <span className="text-muted">{!course?.is_online ? 'In-Person' : 'Online'}</span>
                      <div className="course-detail-row text-info">
                        <div className="detail-icon h5">
                          <FaMapMarkerAlt />
                        </div>
                        <div className="detail-text">
                          <span className="h5 text-uppercase fw-bold">{course?.venue.title}</span>
                          <br />
                          <span className="fw-bold">{formatAddress(course?.venue)}</span>
                        </div>
                      </div>
                      <div className="course-detail-row align-items-center fw-bold h5">
                        <div className="detail-icon">
                          <BsClock />
                        </div>
                        <div className="detail-text">
                      <span>
                        {course?.day_of_week} {courseTimeDisplay}
                      </span>
                        </div>
                      </div>
                      <div className="course-detail-row align-items-center fw-bold h5">
                        <div className="detail-icon">
                          <LuChefHat />
                        </div>
                        <div className="detail-text">
                          <span>{`${classesRemaining.length} of ${course.lessons.length} classes remaining`}</span>
                        </div>
                      </div>
                        {course.payment_type !== 'onsite' && (
                            <div className="course-detail-row align-items-center h5">
                                <div className="detail-icon">
                                    <FaTag />
                                </div>
                                <div className="detail-text">
                                    {(() => {
                                        const fullTuition = course.lessons.length * course.price_per_class;
                                        const proratedTuition = classesRemaining.length * course.price_per_class;
                                        const isProrated = course.can_be_prorated && classesRemaining.length < course.lessons.length;
                                        if (isProrated) {
                                            return (
                                                <>
                                                    <span className="fw-bold">Tuition price is </span>
                                                    <s style={{ color: '#5E6469BF' }}>{formatToCurrency(fullTuition)}</s>{' '}
                                                    <span className="fw-bold text-primary">
                                                      {formatToCurrency(proratedTuition)}
                                                    </span>{' '}
                                                    <span style={{ color: '#5E6469BF' }}>
                                                      (prorated for {classesRemaining.length} of {course.lessons.length} classes)
                                                    </span>
                                                </>
                                            );
                                        }

                                        return (
                                            <>
                                                <span className="fw-bold">Tuition price is </span>
                                                <span className="fw-bold">{formatToCurrency(fullTuition)} </span>
                                                <span style={{ color: '#5E6469BF' }}>
                                                  ({formatToCurrency(course.price_per_class)}/class)
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                        {course.payment_type !== 'onsite' && (
                            <CourseRegisterButton
                                course={course!}
                                addChild={() =>
                                    addChild({
                                        closeConfirmation: true,
                                        getProfile: true
                                    })
                                }
                                registerCourse={() => setShowRegister(true)}
                                userChildren={userProfile?.user?.children ?? []}
                            />
                        )}
                        {/*<AddFavorite course_id={course.id} />*/}
                    </Stack>
                  </Col>
                </Row>
                <CourseDetailsAccordion course={course} />
              </>
          )}
        </div>
        <Modal
            size="xl"
            backdrop={'static'}
            keyboard={true}
            show={showAddChild}
            onEscapeKeyDown={() => hideOverlay(false)}
        >
          <Modal.Header>
            <Modal.Title>Add Child</Modal.Title>
            <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => hideOverlay(false)}
            />
          </Modal.Header>
          <Modal.Body>
            <AddChild
                isModal={true}
                hideOverlay={() => hideOverlay(true)}
            />
          </Modal.Body>
        </Modal>
        <Modal
            centered={true}
            backdrop={'static'}
            keyboard={true}
            show={showChildConfirmation}
            onEscapeKeyDown={() => hideChildConfirmation()}
            dialogClassName="confirmation-modal"
        >
          <Modal.Body>
            <CourseChildConfirmation
                hideOverlay={hideChildConfirmation}
                addChild={() =>
                    addChild({
                      closeConfirmation: true,
                      getProfile: true
                    })
                }
                registerChild={() => {
                  setShowChildConfirmation(false);
                  refetch();
                  setShowAddChild(false);
                  setShowRegister(true);
                }}
            />
          </Modal.Body>
        </Modal>
        <Modal
            centered={true}
            backdrop={'static'}
            keyboard={true}
            show={showRegister}
            onEscapeKeyDown={() => hideRegister()}
            dialogClassName="register-modal"
        >
          <Modal.Body>
              <CourseRegister
                  course={course!}
                  userChildren={userProfile?.user?.children ?? []}
                  hideOverlay={hideRegister}
                  classesRemaining={classesRemaining}
                  selectedChildIds={registerSelectedChildIds}
                  onSelectedChildIdsChange={setRegisterSelectedChildIds}
                  addChild={() =>
                      addChild({
                          closeRegister: true
                      })
                  }
              />
          </Modal.Body>
        </Modal>
      </>
  );
};

export default CourseDetails;
